import { Primitive } from '@radix-ui/react-primitive';
import { type ComponentPropsWithoutRef, memo, useEffect, useMemo } from 'react';
import { type DragSourceMonitor, useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { type LibraryWithPermissions } from '@air/api/types';
import { cn } from '@air/utils-tailwind';

import { type DragLibraryItem, DragType } from '~/components/Shared/Drag/dragTypes';

export type DNDLibraryDragProps = ComponentPropsWithoutRef<typeof Primitive.div> & {
  library: LibraryWithPermissions;
  enabled?: boolean;
  index: number;
};

export const DNDLibraryDrag = memo(
  ({ library, children, enabled = true, index, className, ...restOfProps }: DNDLibraryDragProps) => {
    const dragItem: DragLibraryItem = useMemo(
      () => ({
        type: DragType.library,
        id: library.id,
        index,
        library,
      }),
      [index, library],
    );

    const [{ isDragging }, drag, preview] = useDrag({
      item: dragItem,
      canDrag: enabled,
      collect: (monitor: DragSourceMonitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: () => {
        // Force the browser to reset its HTML5 drag cursor state
        // Without this, the "grabbing" cursor persists when moving to the gallery
        // This is a workaround for a browser HTML5 drag API issue
        const mouseUpEvent = new MouseEvent('mouseup', {
          bubbles: true,
          cancelable: true,
          view: window,
        });
        document.dispatchEvent(mouseUpEvent);
      },
    });

    useEffect(() => {
      preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    return (
      <Primitive.div
        className={cn('data-[dnd-dragging=true]:opacity-50', className)}
        data-dnd-dragging={isDragging}
        data-draggable="true"
        draggable={enabled}
        {...restOfProps}
        ref={drag}
      >
        {children}
      </Primitive.div>
    );
  },
);

DNDLibraryDrag.displayName = 'DNDLibraryDrag';
